/**
 * Store-ID resolution for the UK store_id_m used by the Core List feature.
 *
 * Different env → different `store_id_m` for the same logical store:
 *   - dev / staging → 747
 *   - production    → 873
 *
 * There's no dedicated `VITE_ENV` flag in this repo (see CLAUDE.md / env vars).
 * The only env signal we can rely on is `VITE_API_URL`:
 *   - staging  → https://staging-api.retail-circle.io
 *   - prod     → https://daowatches.com/api  (or https://api.retail-circle.io)
 *
 * Detection rule: if VITE_API_URL contains "staging" → staging (747).
 * Otherwise → production (873). `import.meta.env.DEV` is treated as staging
 * because the dev proxy targets staging-api by default (see vite.config.js).
 */

const STORE_ID_M_STAGING = 747;
const STORE_ID_M_PROD    = 873;

export const isStagingEnv = () => {
  // An explicit declaration wins over every guess below. Each env file states
  // which environment it is (`VITE_API_ENV=staging|production`), so
  // `vite --mode production` against the production API resolves the
  // PRODUCTION store — previously impossible, because the `import.meta.env.DEV`
  // check below is true for ANY dev server whatever `--mode` says, so a
  // dev build pointed at production asked it for the staging store's data.
  const declared = import.meta.env.VITE_API_ENV;
  if (declared) return String(declared).toLowerCase() !== "production";

  // Vite dev server proxies to staging-api → treat dev as staging.
  if (import.meta.env.DEV) return true;

  const apiUrl = import.meta.env.VITE_API_URL || "";
  return apiUrl.toLowerCase().includes("staging");
};

/**
 * Returns the UK store_id_m for the current env.
 *   - 747 in dev / staging
 *   - 873 in production
 */
export const getUkStoreIdM = () =>
  isStagingEnv() ? STORE_ID_M_STAGING : STORE_ID_M_PROD;

/**
 * True for RC/DAO staff: an RC master admin, or the CURRENT store (active
 * store if the caller has switched, else their own store_id_m) being the
 * DAO/RC-master store. This is the gate for DAO Watches' proprietary market
 * intelligence (PriceIQ, price analysis / price analytics) — data tenants
 * have no rights to and no data of their own for. Never compare a hardcoded
 * store id; the env-aware id lives in getUkStoreIdM().
 *
 * Phase 5 of the multi-store access rework: pass `activeStoreId` from
 * UserContext (`useContext(UserContext).activeStoreId`) whenever it's
 * available so this follows a store switch — a merchant admin who switches
 * INTO the DAO store must see PriceIQ there, and an RC/DAO staffer who
 * switches AWAY from it must lose it for that request, matching what the
 * backend actually scopes. Omitting activeStoreId (or passing null/undefined)
 * falls back to user.store_id_m — the original, pre-switch behaviour — so
 * every existing call site keeps working unchanged until it's updated to
 * pass the active store too.
 *
 * Single source of truth — every caller MUST use this, not duplicate the
 * predicate (see git history: it used to be duplicated in
 * order/components/Quote/QuoteView.jsx and the "Dao Watches" sidebar group
 * in layouts/RCAdmin/Sidebar.jsx, which is exactly how it could drift).
 */
export const isRcOrDaoStaff = (user, activeStoreId = null) => {
  if (user?.is_master_admin) {
    return true;
  }

  const currentStoreId = activeStoreId ?? user?.store_id_m ?? null;
  return currentStoreId === getUkStoreIdM();
};
